import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { verifyPayment, initiatePayment } from '../api';
import { useCart } from '../context/CartContext';
import { FiCheckCircle, FiXCircle, FiLoader, FiArrowRight, FiRefreshCw } from 'react-icons/fi';
import './PaymentCallback.css';

const NPR = (n) => 'NPR ' + parseFloat(n || 0).toLocaleString('en-NP');

/**
 * Where Khalti and Fonepay send the payer back to.
 *
 * The query string here is only a hint — it is the server that re-checks the
 * payment with the gateway before anything is marked paid. This page just
 * relays those parameters and reports the verdict.
 */
const PaymentCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { clearCart } = useCart();

  const [state, setState] = useState('verifying'); // verifying | success | failed
  const [detail, setDetail] = useState('');
  const [order, setOrder] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const verifiedRef = useRef(false); // React 18 StrictMode runs effects twice

  useEffect(() => {
    if (verifiedRef.current) return;
    verifiedRef.current = true;

    const params = Object.fromEntries(searchParams.entries());
    const stored = (() => {
      try { return JSON.parse(sessionStorage.getItem('pending_payment')) || {}; }
      catch { return {}; }
    })();

    // Khalti returns `pidx`; Fonepay returns `PRN` + a `DV` signature.
    const provider = params.pidx ? 'khalti' : (params.PRN || params.DV) ? 'fonepay' : stored.provider;

    if (!provider) {
      setState('failed');
      setDetail('This payment link is missing its details. If money left your account, contact us with your transaction ID.');
      return;
    }

    verifyPayment({ provider, params, payment_id: stored.payment_id })
      .then(({ data }) => {
        setState('success');
        setDetail(data.detail || 'Payment verified.');
        setOrder(data.order || stored.order || null);
        sessionStorage.removeItem('pending_payment');
        clearCart().catch(() => {});
      })
      .catch((err) => {
        setState('failed');
        setDetail(
          err.response?.data?.detail
          || 'We could not confirm this payment. You have not been charged for an unconfirmed order.'
        );
        setOrder(stored.order || null);
      });
    // Runs once on mount; the ref above guards StrictMode's double invocation.
  }, []);

  const handleRetry = async () => {
    if (!order?.id) { navigate('/cart'); return; }
    setRetrying(true);
    try {
      const stored = JSON.parse(sessionStorage.getItem('pending_payment') || '{}');
      const { data } = await initiatePayment({
        order: order.id,
        provider: stored.provider || (searchParams.get('pidx') ? 'khalti' : 'fonepay'),
      });
      window.location.href = data.payment_url;
    } catch {
      setRetrying(false);
      navigate('/dashboard');
    }
  };

  return (
    <div className="paycb-page">
      <div className="container paycb-container">
        {state === 'verifying' && (
          <div className="card paycb-card">
            <FiLoader className="paycb-icon spin" />
            <h1>Confirming your payment…</h1>
            <p className="text-muted">
              We're checking with your payment provider. This takes a moment — please don't close this page.
            </p>
          </div>
        )}

        {state === 'success' && (
          <div className="card paycb-card success">
            <FiCheckCircle className="paycb-icon" />
            <h1>Payment successful</h1>
            <p className="text-muted">{detail}</p>

            {order && (
              <div className="paycb-order">
                <div className="paycb-row">
                  <span>Order</span>
                  <strong>#{order.id}</strong>
                </div>
                {order.tracking_id && (
                  <div className="paycb-row">
                    <span>Tracking ID</span>
                    <strong><code>{order.tracking_id}</code></strong>
                  </div>
                )}
                {order.total && (
                  <div className="paycb-row">
                    <span>Amount paid</span>
                    <strong>{NPR(order.total)}</strong>
                  </div>
                )}
              </div>
            )}

            <div className="paycb-actions">
              <Link to="/dashboard" className="btn btn-primary">View My Orders <FiArrowRight /></Link>
              <Link to="/products" className="btn btn-outline">Continue Shopping</Link>
            </div>
          </div>
        )}

        {state === 'failed' && (
          <div className="card paycb-card failed">
            <FiXCircle className="paycb-icon" />
            <h1>Payment not completed</h1>
            <p className="text-muted">{detail}</p>
            <p className="paycb-help">
              Your order is saved and unpaid — nothing has shipped. You can try the payment again,
              or pick a different method from your orders.
            </p>

            <div className="paycb-actions">
              <button className="btn btn-primary" onClick={handleRetry} disabled={retrying}>
                <FiRefreshCw /> {retrying ? 'Starting…' : 'Try Again'}
              </button>
              <Link to="/dashboard" className="btn btn-outline">Go to My Orders</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentCallback;
